import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';

import type { IQueryKeyFactory } from '~/helpers/types';
import type { ApiError } from '~/lib/api';
import type {
  IApiGetAdsRequestBody,
  IApiGetAdsResponseBody,
} from '../AdsApi.types';
import { useApi } from '~/lib/api/useApi';
import { adQueryKeyFactory } from './useAdQuery';

export type IUseAdsQueryValues = {
  appId: string;
  body?: IApiGetAdsRequestBody;
};

export const adsQueryKeyFactory: IQueryKeyFactory<IUseAdsQueryValues> = (
  values,
) =>
  values &&
  (values.body
    ? (['ads', values.appId, values.body] as const)
    : (['ads', values.appId] as const));

export const useAdsQuery = (
  values?: IUseAdsQueryValues,
  options?: UseQueryOptions<IApiGetAdsResponseBody, ApiError>,
): UseQueryResult<IApiGetAdsResponseBody, ApiError> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: adsQueryKeyFactory(values),
    queryFn: values
      ? () => api.ads.getAll(values.appId, values.body)
      : undefined,
    onSuccess: (ads) => {
      ads.list?.forEach((ad) => {
        const queryKey = adQueryKeyFactory({
          appId: ad.appId,
          adId: ad._id,
        });
        if (queryKey) {
          queryClient.setQueryData(queryKey, ad);
        }
      });
      options?.onSuccess?.(ads);
    },
    ...options,
  });
};
